/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import installmentReportsService from 'service/installmentReports';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import MonthlyAdvanceTable from 'components/base/MonthlyAdvanceTable';
import './monthly.scss';

interface MonthlyEmployeeTableProps {
  selectedCurrencyPid?: number;
  dateFrom: any;
  dateTo: any;
  selectedStatusPid?: number;
  selectedCustomerPid?: number;
}
const MonthlyEmployeeTable = ({
  selectedCurrencyPid,
  dateFrom,
  dateTo,
  selectedStatusPid,
  selectedCustomerPid
}: MonthlyEmployeeTableProps) => {
  const { t, i18n } = useTranslation();

  /* prettier-ignore */
  const adjustedDateTo =
  !dateFrom && !dateTo
    ? undefined
    : dateTo === dateFrom
      ? dateFrom + 86400000
      : dateTo
        ? dateTo + 86400000 
        : undefined;

  const { data } = useQuery(
    [
      'GET_INSTALLMENT_MONTHLY',
      adjustedDateTo,
      dateFrom,
      selectedCurrencyPid,
      selectedStatusPid,
      selectedCustomerPid
    ],
    () => {
      const res = installmentReportsService
        .getListInstallmentMonthly({
          date_from: dateFrom,
          date_to: adjustedDateTo,
          currency_pid: selectedCurrencyPid,
          status: selectedStatusPid,
          customer_id: selectedCustomerPid
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!selectedCurrencyPid ||
        !!dateFrom ||
        !!adjustedDateTo ||
        !!selectedStatusPid ||
        !!selectedCustomerPid
    }
  );

  const monthlyTableColumns: ColumnDef<any>[] = useMemo(() => {
    const columns: ColumnDef<any>[] = [
      {
        accessorKey: 'day',
        header: t('day'),
        meta: {
          headerProps: { style: { width: '5%' } },
          cellProps: { className: 'text-900' },
          footerProps: { className: 'text-900' }
        },
        enableSorting: true,
        cell: ({ row }) => <div className="day-cell">{row.original.day}</div>,
        footer: () => null
      }
    ];

    data?.columns_with_name?.forEach((month: any, index: number) => {
      const monthKey = data.columns[index];
      const summary = data?.monthly_summary?.[monthKey] || {};

      columns.push({
        accessorKey: `month_${index}`,
        header: i18n.language === 'uz' ? month.month_uz : month.month_ru,
        meta: {
          headerProps: { style: { width: '20%' } },
          cellProps: { className: 'text-900' },
          footerProps: { className: 'text-900' }
        },

        enableSorting: true,
        cell: ({ row }) => {
          const dayData = row.original.data;
          const cellData = dayData?.cells[index]?.installments || [];
          return cellData.length > 0 ? (
            <div className="client-container">
              {cellData.map((inst: any, i: number) => {
                let buttonClass = 'client-button';
                switch (inst.status) {
                  case 'PAID':
                    buttonClass += ' paid';
                    break;
                  case 'WAITING':
                    buttonClass += ' waiting';
                    break;
                  case 'EXPIRED':
                    buttonClass += ' expired';
                    break;
                  case 'PARTIALLY_PAID':
                    buttonClass += ' partially_paid';
                    break;
                  default:
                    buttonClass += ' default';
                }

                const nameParts = inst.customer_name.trim().split(' ');
                const abbreviatedName =
                  nameParts.length > 1
                    ? `${nameParts[0]} ${nameParts[1].charAt(0)}.`
                    : inst.customer_name;

                // Format price with spaces (e.g., 23100 to 23 100)
                const formattedPrice = inst.monthly_amount
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

                // State to control tooltip visibility
                const [isTooltipVisible, setIsTooltipVisible] = useState(false);

                return (
                  <div
                    key={i}
                    className={buttonClass}
                    onMouseEnter={() => setIsTooltipVisible(true)}
                    onMouseLeave={() => setIsTooltipVisible(false)}
                  >
                    {abbreviatedName}
                    {isTooltipVisible && (
                      <div className="tooltip-content">
                        <p>
                          {t('name')}: {inst.customer_name}
                        </p>
                        <p>
                          {t('phone')}: {inst.customer_phone}
                        </p>
                        <p>
                          {t('price')}: {formattedPrice}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="empty-cell"></span>
          );
        },
        footer: () => {
          const cycle1Count = summary.expired_count || 0; // Replaced expired
          const cycle1Amount = summary.expired_amount || 0;
          const cycle2Count = summary.paid_count || 0; // Replaced expired
          const cycle2Amount = summary.paid_amount || 0;
          const cycle3Count = summary.coming_count || 0; // Replaced waiting
          const cycle3Amount = summary.coming_amount || 0;
          const cycle4Count = summary.partially_paid_count || 0;
          const cycle4Amount = summary.partially_paid_amount || 0;
          const cycle5Count = 0; // Replaced default
          const cycle5Amount = 0;

          const totalCounts = summary.total_count || 0;
          const totalAmounts = summary.total_amount || 0;

          // const formatAmount = (amount: number) =>
          //   amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

          return (
            <>
              <div className="summary-footer-row">
                <div className="summary-cell cycle1">
                  <div>
                    <span className="status-indicator"></span> {cycle1Count}{' '}
                  </div>
                  <span>{cycle1Amount}</span>
                </div>
                <div className="summary-cell cycle2">
                  <div>
                    <span className="status-indicator"></span> {cycle2Count}{' '}
                  </div>
                  <span>{cycle2Amount}</span>
                </div>
                <div className="summary-cell cycle3">
                  <div>
                    <span className="status-indicator"></span> {cycle3Count}{' '}
                  </div>
                  <span>{cycle3Amount}</span>
                </div>
                <div className="summary-cell cycle4">
                  <div>
                    <span className="status-indicator"></span> {cycle4Count}{' '}
                  </div>
                  <span>{cycle4Amount}</span>
                </div>
                <div className="summary-cell cycle5">
                  <div>
                    <span className="status-indicator"></span> {cycle5Count}{' '}
                  </div>
                  <span>{cycle5Amount}</span>
                </div>
              </div>
              <div className="line"></div>
              <div className="total-summary">
                <span>{totalCounts}</span>
                <span>{totalAmounts}</span>
              </div>
            </>
          );
        }
      });
    });

    return columns;
  }, [data, i18n.language, t]);

  const monthlyData: any[] = useMemo(() => {
    const days = Array.from({ length: 31 }, (_, i) => i + 1); // Days 1 to 31
    return days.map(day => {
      const dayRow = data?.day_rows?.find((row: any) => row.day === day);
      return {
        day,
        data: dayRow || { cells: [] } // Ensure cells exist even if no data for the day
      };
    });
  }, [data]);

  const table = useAdvanceTable({
    data: monthlyData,
    columns: monthlyTableColumns,
    pageSize: 50,
    pagination: true,
    sortable: false,
    selection: false,
    hasFooter: true
  });

  return (
    <>
      <AdvanceTableProvider {...table}>
        <div className="monthly-table mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
          <MonthlyAdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            hasFooter={true}
          />
        </div>
      </AdvanceTableProvider>
    </>
  );
};

export default MonthlyEmployeeTable;
